import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import {
  GlobeHemisphereWest, Phone, EnvelopeSimple, User, Storefront,
  ArrowSquareOut, ArrowClockwise, MapPin, CaretDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { PROJECT_TYPES } from "../telepro/WebsiteLeadModal";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const FOUND_VIA_LABELS = {
  google: "Google", facebook: "Facebook", instagram: "Instagram",
  bouche_a_oreille: "Bouche à oreille", site_web: "Leur site web", autre: "Autre",
};
const INTEREST_LABELS = {
  interested: "Intéressé", thinking: "À réfléchir", not_interested: "Pas intéressé",
};

// Pipeline Digital Corp France — order matters (left → right = progression).
const LEAD_STATUSES = [
  { id: "to_contact", label: "À contacter", color: "bg-muted text-foreground border-border" },
  { id: "contacted",  label: "Contacté",    color: "bg-[#E0B043]/15 text-[#8a6a14] border-[#E0B043]/40" },
  { id: "quoted",     label: "Devis envoyé", color: "bg-[#1a5f8a]/15 text-[#1a5f8a] border-[#1a5f8a]/40" },
  { id: "signed",     label: "Signé",       color: "bg-primary/15 text-primary border-primary/40 font-bold" },
  { id: "lost",       label: "Perdu",       color: "bg-secondary/15 text-secondary border-secondary/40" },
];
const LEAD_STATUS_MAP = Object.fromEntries(LEAD_STATUSES.map((s) => [s.id, s]));

export default function SiteWebLeadsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const r = await api.get("/admin/site-web-leads", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (biz_id, status) => {
    try {
      await api.patch(`/admin/site-web-leads/${biz_id}/status`, { status });
      toast.success(`Lead → ${LEAD_STATUS_MAP[status].label}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const markSignedWithAmount = async (biz_id, telepro_name) => {
    const raw = window.prompt(
      `Lead signé — Saisis le montant TTC facturé pour le site (en €, point décimal).\n\n` +
      `La commission de 10% sera automatiquement attribuée à ${telepro_name || "le télépro"}.`,
      ""
    );
    if (raw === null) return;
    const amount = parseFloat(String(raw).replace(",", "."));
    if (!amount || amount <= 0 || isNaN(amount)) {
      toast.error("Montant TTC invalide — réessaie.");
      return;
    }
    try {
      const r = await api.patch(`/admin/site-web-leads/${biz_id}/status`, {
        status: "signed", amount_ttc_eur: amount,
      });
      const comm = r?.data?.commission_created;
      if (comm) {
        toast.success(
          `Signé ! Commission de ${comm.amount_eur.toFixed(2)} € attribuée à ${comm.telepro_code}.`,
          { duration: 6000 }
        );
      } else {
        toast.success("Lead marqué comme signé.");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (!data) return <div className="text-sm text-muted-foreground py-6 text-center">Chargement…</div>;

  const counts = data.counts || {};
  const totalAll = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="admin-site-web-leads">
      <div className="bg-card border border-border rounded-sm p-4 mb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="label-uppercase text-[#1a5f8a] flex items-center gap-1.5 mb-1">
              <GlobeHemisphereWest size={14} weight="fill"/> Pipeline Digital Corp France
            </div>
            <h2 className="font-display text-lg font-medium">Leads sites web</h2>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl leading-relaxed">
              Prospects que tes télépros ont flaggés « Besoin site web ». Utilise les statuts pour suivre où chacun en est dans ton pipeline commercial.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Total pipeline</div>
            <div className="font-display text-3xl tabular-nums font-medium text-[#1a5f8a]" data-testid="site-web-leads-count">{totalAll}</div>
            <button
              onClick={load}
              disabled={loading}
              className="text-[11px] text-primary hover:underline mt-1 inline-flex items-center gap-1"
              data-testid="site-web-leads-refresh"
            >
              <ArrowClockwise size={10} weight="bold" className={loading ? "animate-spin" : ""}/> Rafraîchir
            </button>
          </div>
        </div>
      </div>

      {/* Filter tabs — kanban-style */}
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="lead-status-filter">
        <button
          onClick={() => setFilter("all")}
          className={`text-xs font-semibold border rounded-sm px-3 py-1.5 transition ${
            filter === "all" ? "bg-foreground text-background border-foreground" : "bg-card border-border hover:border-foreground"
          }`}
          data-testid="lead-filter-all"
        >
          Tous <span className="tabular-nums opacity-80 ml-1">{totalAll}</span>
        </button>
        {LEAD_STATUSES.map((s) => (
          <button
            key={s.id}
            onClick={() => setFilter(s.id)}
            className={`text-xs font-semibold border rounded-sm px-3 py-1.5 transition ${
              filter === s.id ? s.color + " ring-2 ring-offset-1 ring-current" : "bg-card border-border hover:border-foreground"
            }`}
            data-testid={`lead-filter-${s.id}`}
          >
            {s.label} <span className="tabular-nums opacity-80 ml-1">{counts[s.id] || 0}</span>
          </button>
        ))}
      </div>

      {data.items.length === 0 ? (
        <div className="bg-accent/20 border border-dashed border-border rounded-sm p-12 text-center" data-testid="site-web-leads-empty">
          <GlobeHemisphereWest size={32} className="mx-auto text-muted-foreground mb-2" weight="duotone"/>
          <div className="text-sm font-semibold">
            {filter === "all" ? "Aucun lead pour le moment." : `Aucun lead au statut « ${LEAD_STATUS_MAP[filter]?.label} ».`}
          </div>
          <div className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            {filter === "all"
              ? "Dès qu'un télépro clique « Besoin site web » sur un prospect pendant un appel, il apparaîtra ici."
              : "Change le filtre ci-dessus pour voir les autres statuts."}
          </div>
        </div>
      ) : (
        <div className="grid gap-3">
          {data.items.map((l) => {
            const s = LEAD_STATUS_MAP[l.lead_status] || LEAD_STATUSES[0];
            return (
              <div key={l.business.id} className="bg-card border border-border rounded-sm p-4" data-testid={`site-web-lead-${l.business.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Storefront size={16} weight="duotone" className="text-[#1a5f8a] shrink-0"/>
                      <span className="font-display font-semibold">{l.business.name}</span>
                      {l.business.category && <span className="text-xs text-muted-foreground">· {l.business.category}</span>}
                      {l.business.slug && (
                        <Link
                          to={`/entreprise/${l.business.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:underline text-xs inline-flex items-center gap-0.5"
                          data-testid={`site-web-view-fiche-${l.business.id}`}
                        >
                          Fiche <ArrowSquareOut size={10} weight="bold"/>
                        </Link>
                      )}
                      {/* Status pill dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={`inline-flex items-center gap-1 border rounded-sm px-2.5 py-0.5 text-[11px] ml-auto ${s.color}`}
                            data-testid={`lead-status-btn-${l.business.id}`}
                          >
                            {s.label} <CaretDown size={10} weight="bold"/>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          {LEAD_STATUSES.map((opt) => (
                            <DropdownMenuItem
                              key={opt.id}
                              onClick={() => {
                                if (opt.id === "signed") {
                                  markSignedWithAmount(l.business.id, l.telepro?.name);
                                } else {
                                  changeStatus(l.business.id, opt.id);
                                }
                              }}
                              data-testid={`lead-status-opt-${l.business.id}-${opt.id}`}
                            >
                              {opt.label}
                              {opt.id === l.lead_status && <span className="ml-auto text-primary">✓</span>}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mb-3">
                      <MapPin size={11} weight="duotone"/>
                      {l.business.address || "—"}, {l.business.postal_code} {l.business.city}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-2 mb-2 text-xs">
                      <div className="bg-primary/5 border border-primary/20 rounded-sm p-2">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-0.5">📞 Contact prospect (à rappeler)</div>
                        <div className="space-y-0.5">
                          {l.contact_name && <div><User size={10} weight="duotone" className="inline mr-1"/><b>{l.contact_name}</b></div>}
                          {l.contact_phone && (
                            <div>
                              <Phone size={10} weight="bold" className="inline mr-1 text-primary"/>
                              <a href={`tel:${l.contact_phone.replace(/\s/g,"")}`} className="font-mono text-primary hover:underline">{l.contact_phone}</a>
                            </div>
                          )}
                          {l.contact_email && (
                            <div>
                              <EnvelopeSimple size={10} weight="bold" className="inline mr-1 text-primary"/>
                              <a href={`mailto:${l.contact_email}`} className="font-mono text-primary hover:underline">{l.contact_email}</a>
                            </div>
                          )}
                          {!l.contact_name && !l.contact_phone && !l.contact_email && (
                            <div className="text-muted-foreground italic">Aucune coordonnée capturée — rappelle via le numéro pro ci-dessous.</div>
                          )}
                        </div>
                      </div>
                      <div className="bg-muted/30 border border-border rounded-sm p-2">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-0.5">Coordonnées entreprise (annuaire)</div>
                        <div className="space-y-0.5">
                          {l.business.phone && (
                            <div>
                              <Phone size={10} weight="bold" className="inline mr-1"/>
                              <a href={`tel:${l.business.phone.replace(/\s/g,"")}`} className="font-mono hover:underline">{l.business.phone}</a>
                            </div>
                          )}
                          {l.business.email && (
                            <div>
                              <EnvelopeSimple size={10} weight="bold" className="inline mr-1"/>
                              <a href={`mailto:${l.business.email}`} className="font-mono hover:underline">{l.business.email}</a>
                            </div>
                          )}
                          {l.business.website && (
                            <div className="italic text-muted-foreground">Note : ils ont un site sur l&apos;annuaire ({l.business.website}) — vérifie sa qualité.</div>
                          )}
                          {!l.business.phone && !l.business.email && !l.business.website && (
                            <div className="text-muted-foreground italic">Aucune info annuaire.</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {l.website_lead && (
                      <div className="text-xs bg-[#1a5f8a]/5 border-l-2 border-[#1a5f8a] rounded-sm p-2 mb-2 space-y-0.5" data-testid={`site-web-project-${l.business.id}`}>
                        <div><b className="text-[#1a5f8a]">Projet :</b> {(l.website_lead.project_types || []).map((t) => PROJECT_TYPES.find((p) => p.id === t)?.label || t).join(", ")}</div>
                        <div><b>Budget :</b> {l.website_lead.budget || "non communiqué"}</div>
                        <div><b>Disponibilité :</b> {l.website_lead.availability || "—"}</div>
                        {l.website_lead.comments && <div><b>Commentaires :</b> <i>{l.website_lead.comments}</i></div>}
                        {l.website_lead.consent && <div className="text-[10px] text-primary font-semibold">✓ Accord du prospect pour être recontacté</div>}
                      </div>
                    )}

                    {l.note && (
                      <div className="text-xs bg-[#E0B043]/10 border-l-2 border-[#E0B043] rounded-sm p-2 mb-2" data-testid={`site-web-note-${l.business.id}`}>
                        <b className="text-[#8a6a14]">Note télépro :</b> <i>{l.note}</i>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-1 text-[10px]">
                      {(l.prospect_answers?.found_via || []).map((v) => (
                        <span key={v} className="bg-accent/40 border border-border rounded-sm px-1.5 py-0.5">
                          Trouvé via : {FOUND_VIA_LABELS[v] || v}
                        </span>
                      ))}
                      {l.prospect_answers?.interest && (
                        <span className="bg-primary/10 border border-primary/30 text-primary rounded-sm px-1.5 py-0.5 font-semibold">
                          {INTEREST_LABELS[l.prospect_answers.interest] || l.prospect_answers.interest}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="lg:w-52 shrink-0 border-t lg:border-t-0 lg:border-l border-border pt-3 lg:pt-0 lg:pl-4">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Flaggé par</div>
                    <div className="font-semibold text-sm mb-1">{l.telepro.name || "—"}</div>
                    <div className="text-[11px] font-mono text-primary">{l.telepro.code}</div>
                    <div className="text-[10px] text-muted-foreground mt-2">
                      Le {l.flagged_at ? new Date(l.flagged_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </div>
                    {l.lead_status_at && l.lead_status !== "to_contact" && (
                      <div className="text-[10px] text-muted-foreground mt-1">
                        Statut MàJ : {new Date(l.lead_status_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
